import { debounce, throttle } from 'lodash';
import { invokeEvent, l, toJson } from 'shared-base';
import { parseMaterial } from '../data/data.default.textureMaps';
import { Stage } from '../Viewer/Viewer.types';
import { log } from '../Viewer/Viewer.utils';
import { BridgeMethod, IBridge, ThrottleDefinitions } from './bridge.types';
import { unityContext } from './globals';
import { extractModifiers } from './modifiers';

const BRIDGE_LOG_DEBOUCE = 100;
let currentSlotId: any = null;

const addLog = (instanceId: string, message: string, data?: any): void => {
  const showData = message === 'SetMaterials';
  l(`canvas|${message}`, toJson(data), showData);
};

const addLogDebounced = debounce(addLog, BRIDGE_LOG_DEBOUCE);

const throttles: ThrottleDefinitions = {
  SetValuesModel: 50,
  SetMaterials: 200,
};

const throttledSendMap: Partial<Record<BridgeMethod, (instanceId: string, data?: any) => void>> =
  {};
Object.entries(throttles).forEach(([method, wait]) => {
  throttledSendMap[method as BridgeMethod] = throttle((instanceId: string, data?: any) => {
    bridge.sendMessage(instanceId, method as BridgeMethod, data);
  }, wait);
});

export const bridge: IBridge = {
  log: (instanceId: string, message: string, data?: any): void => {
    if ('debugBridge' in window) addLogDebounced(instanceId, message, data);
  },

  sendMessage: (instanceId: string, message: string, data?: any): void => {
    bridge.log(instanceId, message, data);
    invokeEvent('UnityBridge1', { instanceId, message, data });
  },

  sendMessage$: (instanceId: string, message: BridgeMethod, data?: any): void => {
    const throttled = throttledSendMap[message];
    if (throttled) {
      throttled(instanceId, data);
    } else {
      bridge.sendMessage(instanceId, message, data);
    }
  },

  setSlotId(id: number) {
    currentSlotId = id;
  },

  replaceModel: (instanceId: string, model: IModel): void => {
    delete model.generatorUrl;
    const json = JSON.stringify(extractModifiers(model));
    bridge.sendMessage(instanceId, 'ReplaceModel', json);
  },

  patchValuesModel: (instanceId: string, values: IValues): void => {
    const json = JSON.stringify(values);
    bridge.sendMessage(instanceId, 'PatchValuesModel', json);
  },

  patchWackModifiers: (instanceId: string, modifiers: Record<string, number>): void => {
    const json = JSON.stringify(modifiers);
    bridge.sendMessage(instanceId, 'PatchWackModifiers', json);
  },

  patchEnvironment: (instanceId: string, environment: Partial<IHolodeckState>): void => {
    const json = JSON.stringify(environment);
    bridge.sendMessage(instanceId, 'PatchEnvironment', json);
  },

  resetEnvironment: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'ResetEnvironment');
  },

  patchMaterial: (
    instanceId: string,
    slotId: SlotId = currentSlotId,
    material: Partial<IMaterial> = {}
  ): void => {
    const json = JSON.stringify({ [slotId]: parseMaterial(material) });
    bridge.sendMessage(instanceId, 'PatchMaterial', json);
  },

  patchMaterials: (instanceId: string, materials: Record<SlotId, Partial<IMaterial>>): void => {
    const json = JSON.stringify(materials);
    bridge.sendMessage(instanceId, 'PatchMaterials', json);
  },

  slotShow: (instanceId: string, slot: number) => {
    bridge.sendMessage(instanceId, 'SlotShow', slot);
  },

  slotResetAll: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'SlotResetAll');
  },

  changeShader: (instanceId: string, shader: Shader): void => {
    bridge.sendMessage(instanceId, 'ChangeShader', shader);
  },

  downloadScreenshot: (instanceId: string, generatorId: string): void => {
    bridge.sendMessage(instanceId, 'DownloadScreenshot', { generatorId });
  },

  getScreenshot: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'GetScreenshot');
  },

  postProccessing: (instanceId: string, settings: Partial<IPostProcessing>): void => {
    const json = JSON.stringify(settings);
    bridge.sendMessage(instanceId, 'PostProcessing', json);
  },

  resetPostProccessing: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'ResetPostProcessing');
  },

  cameraMove: (instanceId: string, to: CameraPosition, animation: AnimationInfo): void => {
    const json = JSON.stringify({ to, animation });
    bridge.sendMessage(instanceId, 'CameraMove', json);
  },

  cameraFocus: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'CameraFocus');
  },

  getGlb: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'GetGlb');
  },

  download: (instanceId: string, downloadDefinition: IDownloadSpecification): void => {
    const json = JSON.stringify(downloadDefinition);
    bridge.sendMessage(instanceId, 'Download', json);
  },

  clear: (instanceId: string): void => {
    bridge.sendMessage(instanceId, 'Clear');
  },
};

export const sendToUnity = (message: string, data: any) => {
  log(Stage.UNITY_INSTRUCTIONS);
  if (!unityContext) return;
  unityContext.sendMessage('SloydAPI', message, data);
};
